// Feedback belongs to explicit user actions, never to data subscriptions or imports.
const SOUND_KEY = 'spcc:reaction-sounds';
const CHANGE = 'spcc:feedback-preference';
export const FEEDBACK_EVENT = 'spcc:action-feedback';
let sessionSound = true;
let storageUnavailable = false;
let session = 0;
export const resetFeedbackSession = () => { session++; };
export const soundsEnabled = () => {
  if (storageUnavailable) return sessionSound;
  try { const saved = localStorage.getItem(SOUND_KEY); return saved === null ? sessionSound : saved !== 'off'; } catch { return sessionSound; }
};
export const subscribeSounds = callback => {
  const storage = event => { if (!event.key || event.key === SOUND_KEY) callback(); };
  window.addEventListener(CHANGE, callback); window.addEventListener('storage', storage);
  return () => { window.removeEventListener(CHANGE, callback); window.removeEventListener('storage', storage); };
};
let context, master;
let lastSound = -Infinity;
const NOTES = { resolved: [660, 495], validated: [523.25, 659.25, 783.99], decision: [587.33, 739.99], learned: [523.25, 783.99, 1046.5], evidence: [783.99], saved: [659.25] };

// Called synchronously during a trusted gesture, before the persistence promise.
export function unlockFeedbackAudio() {
  if (!soundsEnabled() || document.hidden) return;
  try {
    const Audio = window.AudioContext || window.webkitAudioContext;
    if (!Audio) return;
    if (!context || context.state === 'closed') {
      context = new Audio(); master = context.createGain(); master.gain.value = 0.18; master.connect(context.destination);
    }
    if (context.state === 'suspended') context.resume()?.catch(() => {});
  } catch { /* Audio is an enhancement; never interrupt a saved action. */ }
}
export function setSoundsEnabled(enabled) {
  sessionSound = Boolean(enabled);
  try { localStorage.setItem(SOUND_KEY, enabled ? 'on' : 'off'); storageUnavailable = false; } catch { storageUnavailable = true; }
  if (master && context) master.gain.setValueAtTime(enabled ? 0.18 : 0, context.currentTime);
  window.dispatchEvent(new Event(CHANGE));
  if (enabled) unlockFeedbackAudio();
}
export function playFeedbackSound(kind) {
  if (!soundsEnabled() || document.hidden || !context || context.state !== 'running' || !NOTES[kind]) return false;
  const now = performance.now();
  if (now - lastSound < 320) return false;
  lastSound = now;
  try {
    master.gain.setValueAtTime(0.18, context.currentTime);
    NOTES[kind].forEach((frequency, i) => {
      const oscillator = context.createOscillator(), gain = context.createGain();
      const start = context.currentTime + i * 0.055, end = start + 0.16;
      oscillator.type = 'sine'; oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0, start); gain.gain.linearRampToValueAtTime(0.09, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.001, end);
      oscillator.connect(gain); gain.connect(master);
      oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
      oscillator.start(start); oscillator.stop(end + 0.01);
    });
    return true;
  } catch { return false; }
}
export function releaseFeedbackAudio() {
  const old = context; context = null; master = null; lastSound = -Infinity;
  try { old?.close()?.catch(() => {}); } catch { /* unavailable audio device */ }
}
export const reducedMotion = () => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
export const pointerMotion = () => document.documentElement.dataset.inputModality === 'pointer' && !reducedMotion();

// Captures modality and target before React releases the event. Each action can
// report completion once. The caller must await a successful database write first.
export function beginFeedback(event, kind) {
  unlockFeedbackAudio();
  const clicked = event?.nativeEvent?.submitter || event?.currentTarget;
  const card = clicked?.closest?.('.issue-card, .problem-card');
  const motion = pointerMotion();
  const startedSession = session;
  let finished = false;
  return {
    async complete(message, detail = '', { remove = kind === 'resolved' } = {}) {
      if (finished || startedSession !== session) return;
      finished = true;
      if (document.hidden) return;
      window.dispatchEvent(new CustomEvent(FEEDBACK_EVENT, { detail: { kind, message, detail, motion: motion && !reducedMotion() } }));
      playFeedbackSound(kind);
      if (!motion || reducedMotion() || !card?.isConnected || !card.animate) return;
      try {
        const easing = getComputedStyle(document.documentElement).getPropertyValue('--ease-out').trim() || 'cubic-bezier(0.23, 1, 0.32, 1)';
        const animation = card.animate(remove
          ? [{ opacity: 1, transform: 'translateY(0) scale(1)' }, { opacity: 0, transform: 'translateY(-4px) scale(.98)' }]
          : [{ transform: 'scale(1)', opacity: 1 }, { transform: 'scale(.985)', opacity: 0.85, offset: 0.3 }, { transform: 'scale(1)', opacity: 1 }],
        { duration: remove ? 180 : 260, easing });
        // A bounded wait prevents unsupported/cancelled animation APIs holding up UI state.
        await Promise.race([animation.finished.catch(() => {}), new Promise(resolve => setTimeout(resolve, 280))]);
        animation.cancel();
      } catch { /* Successful persistence remains successful without animation support. */ }
    },
    error(message) {
      if (finished || startedSession !== session) return;
      finished = true;
      window.dispatchEvent(new CustomEvent(FEEDBACK_EVENT, { detail: { kind: 'error', message, motion: false } }));
    },
  };
}
